import { FeatureCollection, MultiPolygon } from "geojson";
import { Geo } from "@rogoag/geo";
import { AgrianTypes } from "../types/AgrianTypes";

export namespace AgrianGeoFetcher {
	export function getBoundaryGeoJSON(records: AgrianTypes.BoundaryMap[]) {
		const polygons: FeatureCollection[] = [];
		for (const record of records) {
			const geoJSON = Geo.toGeoJSON(record.boundary);
			for (const feature of geoJSON.features) {
				feature.properties = { measure: record.area ?? 0 };
			}
			polygons.push(geoJSON);
		}
		return Geo.merge(polygons);
	}

	export function getSampleEventCentroidsGeoJSON(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): FeatureCollection | undefined {
		const geoJson = getSampleEventSamples(sampleEvent);
		geoJson.features = Geo.separatePointsFromPolygons([geoJson]).points.features;
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export function getSampleEventCoresGeoJSON(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): FeatureCollection | undefined {
		const geoJson = getSampleEventSubSamples(sampleEvent);
		geoJson.features = Geo.separatePointsFromPolygons([geoJson]).points.features;
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export function getSampleEventZonesGeoJSON(
		sampleEvent: AgrianTypes.HydratedSampleEvent,
	): FeatureCollection | undefined {
		const sampleGeoJson = getSampleEventSamples(sampleEvent);
		sampleGeoJson.features = Geo.separatePointsFromPolygons([sampleGeoJson]).polygons.features;

		const subSampleGeoJson = getSampleEventSubSamples(sampleEvent);
		subSampleGeoJson.features = Geo.separatePointsFromPolygons([subSampleGeoJson]).polygons.features;

		const geoJson = Geo.merge([sampleGeoJson, subSampleGeoJson]);
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export function getSampleEventSamples(sampleEvent: AgrianTypes.HydratedSampleEvent): FeatureCollection {
		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const sample of sampleEvent.samples) {
			index++;
			const geoJsonGeometry = Geo.toGeoJSON(sample.geometry);
			if (geoJsonGeometry.features.length > 0) {
				const n = Number(sample.sample_number);
				const sampleNumber = !isNaN(n) ? n : index;
				geoJsonGeometry.features.forEach((feature) => {
					if (!feature.properties) feature.properties = {};
					if (feature.geometry.type === "Point") {
						feature.properties.PointID = sampleNumber;
						if (sample.id) feature.properties.SampleGuid = sample.id;
					} else {
						feature.properties.ZoneID = sampleNumber;
						if (sample.id) feature.properties.ZoneGuid = sample.id;
					}
				});
			}
			geoJsons.push(geoJsonGeometry);
		}

		return Geo.merge(geoJsons);
	}

	export function getSampleEventSubSamples(sampleEvent: AgrianTypes.HydratedSampleEvent): FeatureCollection {
		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const sample of sampleEvent.samples) {
			index++;
			let subIndex = 0;
			for (const subSample of sample.sub_samples) {
				subIndex++;
				const geoJsonGeometry = Geo.toGeoJSON(subSample.geometry);
				if (geoJsonGeometry.features.length > 0) {
					const n = Number(subSample.sub_sample_number);
					const subSampleNumber = !isNaN(n) ? n : `${index}.${subIndex}`;
					geoJsonGeometry.features.forEach((feature) => {
						if (!feature.properties) feature.properties = {};
						if (feature.geometry.type === "Point") {
							feature.properties.PointID = subSampleNumber;
							if (subSample.id) feature.properties.SubSampleGuid = subSample.id;
						} else {
							feature.properties.ZoneID = subSampleNumber;
							if (subSample.id) feature.properties.SubSampleGuid = subSample.id;
						}
					});
				}
				geoJsons.push(geoJsonGeometry);
			}
		}

		return Geo.merge(geoJsons);
	}

	export function getTargetEventCentroidsGeoJSON(
		targetEvent: AgrianTypes.HydratedTargetEvent,
	): FeatureCollection | undefined {
		const geoJson = getTargetEventSamples(targetEvent);
		geoJson.features = Geo.separatePointsFromPolygons([geoJson]).points.features;

		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export function getTargetEventZonesGeoJSON(
		targetEvent: AgrianTypes.HydratedTargetEvent,
	): FeatureCollection | undefined {
		const geoJson = getTargetEventSamples(targetEvent);
		geoJson.features = Geo.separatePointsFromPolygons([geoJson]).polygons.features;

		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}

	export function getTargetEventSamples(event: AgrianTypes.HydratedTargetEvent): FeatureCollection {
		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const sample of event.target_samples) {
			index++;
			const geoJsonGeometry = Geo.toGeoJSON(sample.geometry);
			if (geoJsonGeometry.features.length > 0) {
				const n = Number(sample.sample_number);
				const sampleNumber = !isNaN(n) ? n : index;
				geoJsonGeometry.features.forEach((feature) => {
					if (!feature.properties) feature.properties = {};
					if (feature.geometry.type === "Point") {
						feature.properties.SampleID = sampleNumber;
						if (sample.id) feature.properties.SampleGuid = sample.id;
					} else {
						feature.properties.ZoneID = sampleNumber;
						if (sample.id) feature.properties.ZoneGuid = sample.id;
					}
				});
			}
			geoJsons.push(geoJsonGeometry);
		}

		return Geo.merge(geoJsons);
	}

	export function getZoneMapGeoJSON(zones: AgrianTypes.HydratedZone): FeatureCollection | undefined {
		function convertMultiPolygonToWKT(multipolygon: MultiPolygon): string {
			if (multipolygon.type !== "MultiPolygon") {
				throw new Error("Invalid GeoJSON type. Expected 'MultiPolygon'.");
			}

			const wkt = multipolygon.coordinates
				.map(
					(polygon) => `(${polygon.map((ring) => `(${ring.map((coord) => coord.join(" ")).join(", ")})`).join(", ")})`,
				)
				.join(", ");

			return `MULTIPOLYGON(${wkt})`;
		}

		let index = 0;
		const geoJsons: FeatureCollection[] = [];
		for (const zone of zones.zones) {
			index++;
			const wkt = convertMultiPolygonToWKT(zone.geometry);
			const geoJSON = Geo.toGeoJSON(wkt);
			if (geoJSON.features.length > 0) {
				const n = Number(zone.id);
				const zoneNumber = !isNaN(n) ? n : index;
				geoJSON.features.forEach((feature) => {
					if (!feature.properties) feature.properties = {};
					feature.properties.ZoneID = zoneNumber;
					if (zone.id) feature.properties.ZoneGuid = zone.id;
				});
			}
			geoJsons.push(geoJSON);
		}

		const geoJson = Geo.merge(geoJsons);
		if (geoJson.features.length > 0) {
			return geoJson;
		}
	}
}
